<template lang="pug">
.appointment-summary
  .summary-header
    h3.summary-title {{ headerTitle }}
    .summary-pills
      span.date-pill
        i.el-icon-date
        |  {{ formatDate(new Date()) }}
      span.date-pill
        i.el-icon-time
        |  {{ formatTime(new Date()) }}

  .summary-body(v-if="flatSummary.length")
    .summary-card(v-for="(section, index) in flatSummary" :key="index")
      //- Header de la card: ícono + nombre del tipo + botón opcional
      .summary-card__header
        .summary-card__icon
          i(:class="getSectionIcon(section.type)")
        span.summary-card__title {{ section.title }}
        button.summary-upload-btn(
          v-if="section.type === 'lab' || section.type === 'images'"
          @click="$emit('upload-result', { type: section.type, indicationId: section.indicationId })"
        ) + Resultado

      //- Body: una sub-sección por indicación del mismo tipo
      template(v-for="(item, i) in section.content")
        .summary-card__item(:key="i" :class="{ 'summary-card__item--bordered': section.content.length > 1 && i > 0 }")
          //- Chips para el identificador principal (medicamento, imagen, lab)
          .summary-chip-row(v-if="getChipLines(item).length")
            template(v-for="(line, j) in getChipLines(item)")
              template(v-if="Array.isArray(line.value)")
                span.summary-chip(v-for="(v, k) in line.value" :key="`chip-arr-${j}-${k}`") {{ v }}
              span.summary-chip(v-else :key="`chip-${j}`") {{ line.value }}

          //- Grilla compacta para el resto de campos
          .summary-grid(v-if="getGridLines(item).length")
            .summary-kv(
              v-for="(line, j) in getGridLines(item)"
              :key="j"
              :class="{ 'summary-kv--full': isFullWidth(line.label, line.value) }"
            )
              span.summary-kv__label {{ line.label }}
              span.summary-kv__value(
                :class="{ 'summary-kv__value--clamp': isLongText(line.label) }"
              ) {{ line.value }}

      //- Formularios completados
      completed-forms-list(
        v-if="section.title && section.title.toLowerCase().includes('formulario') && completedForms.length > 0"
        :completedForms="completedForms"
        :showHeader="false"
        :readonly="true"
        @edit-completed-form="onEditCompletedForm"
        @remove-completed-form="onRemoveCompletedForm"
      )
      completed-forms-list(
        v-if="section.programCompletedForms && section.programCompletedForms.length > 0"
        :completedForms="section.programCompletedForms"
        :showHeader="false"
        :readonly="true"
        @edit-completed-form="onEditCompletedForm"
        @remove-completed-form="onRemoveCompletedForm"
      )

  .summary-empty(v-else)
    .summary-empty__icon
      i.el-icon-document
    p.summary-empty__text Aún no hay indicaciones
</template>

<script>
/**
 * @component AppointmentSummary
 * @description Panel lateral compacto del resumen de la atención.
 *              Agrupa todas las indicaciones del mismo tipo en una sola card con
 *              grilla interna de 2 columnas. Los campos principales (medicamento,
 *              imagen, lab) se muestran como chips. Campos largos van a ancho completo.
 * @example <AppointmentSummary :savedIndications="indications" :completedForms="forms" />
 */
import { format } from "date-fns";
import CompletedFormsList from "@/components/ui/CompletedFormsList.vue";

/** @description Labels que renderizan como chips destacados (identificador principal) */
const CHIP_LABELS = ["medicamento", "imágen", "determinación"];

/** @description Labels/keywords que siempre van a ancho completo en la grilla */
const FULL_WIDTH_KEYWORDS = [
  "descripción",
  "evolución",
  "observacion",
  "nota",
  "email",
  "diagnóstico",
  "diagnóstico manual",
];

/** @description Keywords de campos con texto largo — se truncan a 3 líneas */
const LONG_TEXT_KEYWORDS = ["descripción", "evolución", "observacion", "nota"];

/** @description Ícono de sección por tipo de indicación */
const SECTION_ICONS = {
  evolution: "el-icon-document",
  medications: "el-icon-first-aid-kit",
  images: "el-icon-camera",
  lab: "el-icon-receiving",
  practices: "el-icon-document-checked",
  notes: "el-icon-edit",
  certificates: "el-icon-trophy",
  forms: "el-icon-document-copy",
  program: "el-icon-files",
};

export default {
  name: "AppointmentSummary",
  components: { CompletedFormsList },

  props: {
    savedIndications: { type: Array, default: () => [] },
    context: {
      type: String,
      default: "appointment",
      validator: (v) => ["appointment", "prescription"].includes(v),
    },
    completedForms: { type: Array, default: () => [] },
  },

  computed: {
    /**
     * @description Título del header según contexto
     * @returns {string}
     */
    headerTitle() {
      return this.context === "prescription"
        ? "Resumen de prescripciones"
        : "Resumen de atención";
    },

    /**
     * @description Agrupa las indicaciones guardadas en secciones por tipo.
     *              Cada sección tiene title, type, content (array de items)
     *              e indicationId para acciones contextuales.
     * @returns {Array<{title, type, content, indicationId, programCompletedForms}>}
     */
    flatSummary() {
      if (!this.savedIndications || !Array.isArray(this.savedIndications))
        return [];

      const sectionsMap = new Map();

      this.savedIndications.forEach((indication) => {
        let sectionTitle = indication.name;

        if (indication.type === "program") {
          sectionTitle =
            indication.programData?.name ||
            this.getProgramName(indication.defaultValues?.program?.value) ||
            indication.name ||
            "Programa";
        }

        const values = indication.defaultValues || {};
        const lines = Object.entries(values)
          .filter(
            ([, val]) =>
              val.value !== undefined && val.value !== "" && val.value !== 0
          )
          .map(([key, val]) => ({
            label: val.friendlyName || key,
            value: this.getValueFormatted(val.value, val.inputType),
          }));

        if (!lines.length) return;

        if (!sectionsMap.has(sectionTitle)) {
          sectionsMap.set(sectionTitle, {
            type: indication.type,
            indicationId: indication._id || indication.key,
            content: [],
            programCompletedForms: null,
          });
        }

        const section = sectionsMap.get(sectionTitle);
        section.content.push(lines);

        if (indication.type === "program") {
          section.programCompletedForms =
            indication.completedProgramForms || indication.initialData || null;
        }
      });

      return [...sectionsMap.entries()].map(([title, data]) => ({
        title,
        ...data,
      }));
    },
  },

  methods: {
    /**
     * @description Retorna el ícono de sección para el tipo dado
     * @param {string} type - Tipo de indicación
     * @returns {string}
     */
    getSectionIcon(type) {
      return SECTION_ICONS[type] || "el-icon-document";
    },

    /**
     * @description Separa las líneas que son chips (identificador principal)
     * @param {Array} item - Array de { label, value }
     * @returns {Array}
     */
    getChipLines(item) {
      return item.filter((line) =>
        CHIP_LABELS.includes((line.label || "").toLowerCase())
      );
    },

    /**
     * @description Separa las líneas que van en la grilla compacta (no chips)
     * @param {Array} item - Array de { label, value }
     * @returns {Array}
     */
    getGridLines(item) {
      return item.filter(
        (line) => !CHIP_LABELS.includes((line.label || "").toLowerCase())
      );
    },

    /**
     * @description Determina si un campo debe ocupar el ancho completo de la grilla
     * @param {string} label - Label del campo
     * @param {*} value - Valor del campo
     * @returns {boolean}
     */
    isFullWidth(label, value) {
      const lower = (label || "").toLowerCase();
      if (FULL_WIDTH_KEYWORDS.some((k) => lower.includes(k))) return true;
      if (typeof value === "string" && value.length > 28) return true;
      if (Array.isArray(value)) return true;
      return false;
    },

    /**
     * @description Determina si el valor es texto largo y debe truncarse a 3 líneas
     * @param {string} label - Label del campo
     * @returns {boolean}
     */
    isLongText(label) {
      const lower = (label || "").toLowerCase();
      return LONG_TEXT_KEYWORDS.some((k) => lower.includes(k));
    },

    /**
     * @description Formatea un valor según su inputType
     * @param {*} value
     * @param {string} inputType
     * @returns {string|Array}
     */
    getValueFormatted(value, inputType) {
      if (!value) return "—";
      if (inputType === "diagnosisSelect") return this.getDiagnosisName(value);
      if (inputType === "medicationSelect")
        return this.getMedicationName(value);
      if (inputType === "imagesSelect") return this.getImageName(value);
      if (inputType === "labSelect") {
        const v = this.getLabDescription(value);
        return Array.isArray(v) ? v : [v];
      }
      if (inputType === "programSelect") return this.getProgramName(value);
      return value;
    },

    getProgramName(id) {
      const program = this.$store.getters.getProgramById?.(id);
      return program?.name || `Programa: ${id}`;
    },
    getMedicationName(id) {
      const r = this.$store.getters.getMedicationById(id);
      return (Array.isArray(r) ? r[0]?.name : r?.name) || "Desconocido";
    },
    getDiagnosisName(id) {
      const d = this.$store.getters.getDiagnosisById(id);
      return d ? d.description : "Sin datos";
    },
    getImageName(id) {
      const r = this.$store.getters.getImageById(id);
      return (
        (Array.isArray(r) ? r[0]?.description : r?.description) || "Sin datos"
      );
    },
    getLabDescription(codigo) {
      if (Array.isArray(codigo)) {
        return codigo.map((cod) => {
          const lab = this.$store.getters.getLabById(cod);
          return lab ? lab.description : "Sin datos";
        });
      }
      const lab = this.$store.getters.getLabById(codigo);
      return [lab ? lab.description : "Sin datos"];
    },
    formatDate(date) {
      return format(new Date(date), "dd/MM/yyyy");
    },
    formatTime(date) {
      return format(new Date(date), "HH:mm");
    },
    onEditCompletedForm(f) {
      this.$emit("edit-completed-form", f);
    },
    onRemoveCompletedForm(f) {
      this.$emit("remove-completed-form", f);
    },
  },
};
</script>

<style lang="scss" scoped>
.appointment-summary {
  position: sticky;
  top: 24px;
  background: $white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 16px;
  font-family: "Plus Jakarta Sans", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
}

/* ── Header ── */
.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.summary-title {
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0;
}

.summary-pills {
  display: flex;
  gap: 6px;
}

.date-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 7px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;

  i {
    font-size: 11px;
  }
}

/* ── Body ── */
.summary-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Card por tipo ── */
.summary-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: white;
}

.summary-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.summary-card__icon {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: #eef2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 12px;
    color: #475569;
  }
}

.summary-card__title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #002d5e;
  flex: 1;
}

.summary-upload-btn {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 5px;
  border: 1px solid #c8d6e8;
  background: #e1eaf6;
  color: #0a3d7a;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: #d0e0f0;
  }
}

/* ── Item por indicación dentro de la card ── */
.summary-card__item {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &--bordered {
    border-top: 1px dashed #e2e8f0;
  }
}

/* ── Chips (medicamento / imagen / lab) ── */
.summary-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  background: #e6faf6;
  border: 1px solid #9fe1cb;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #0d7a5f;
}

/* ── Grilla de campos ── */
.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 10px;
}

.summary-kv {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  &--full {
    grid-column: 1 / -1;
  }
}

.summary-kv__label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-kv__value {
  font-size: 11px;
  font-weight: 500;
  color: #1e293b;
  word-break: break-word;

  &--clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

/* ── Empty state ── */
.summary-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  gap: 10px;
}

.summary-empty__icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 50%;

  i {
    font-size: 22px;
    color: #94a3b8;
  }
}

.summary-empty__text {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}
</style>
